import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import styles from './OrderItemMobile.module.scss';
import { OrderExpanded } from '@services/checkout';
import { Price, LineItemInfo } from '@components';
import { Modal, Typography } from 'gd-design-library';
import { useRouter } from 'next/router';

const CN = 'order-item-mobile';
const rowClassName = `${CN}__row`;
const imageClassName= `${CN}__image`;
const titleClassName = `${CN}__title`;
const orderNumberClassName = `${CN}__order-number`;
const createdAtClassName = `${CN}__created-at`;
const footerRowClassName = `${CN}__footer-row`;
const modalContentClassName = 'order-item-modal-content';
const itemsSectionClassName = `${modalContentClassName}__items-section`;
const lineItemClassName = `${itemsSectionClassName}__line-item`;
const infoSectionClassName = `${modalContentClassName}__info-section`;
const infoItemClassName = `${infoSectionClassName}__info-item`;

const cx = classnames.bind(styles);

interface Props {
  className?: string;
  order: OrderExpanded;
  imageUrl?: string;
  lineItemQuantity: number;
}

export const OrderItemMobile: React.FC<Props> = ({
  className,
  order: {
    id,
    orderNumber,
    lineItems,
    createdAt,
    orderState,
    totalPrice,
    shippingAddress,
    shippingPrice
  },
  imageUrl,
  lineItemQuantity
}) => {
  const { query } = useRouter();
  const [ isModalOpen, setIsModalOpen ] = useState(false);

  useEffect(() => {
    if (query['order_id'] === id && window.matchMedia('(max-width: 1200px)').matches) {
      setIsModalOpen(true);
    }
  }, [ query, id ]);

  return (
    <>
      <div
        onClick={ (): void => setIsModalOpen(true) }
        className={ `${cx(CN)} ${className}` }
      >
        <div className={ cx(rowClassName) }>
          <Typography className={ cx(orderNumberClassName) }>{orderNumber}</Typography>
          <Typography className={ cx(titleClassName, createdAtClassName) }>
            {new Date(createdAt).toLocaleDateString()} {new Date(createdAt).toLocaleTimeString()}
          </Typography>
        </div>
        <div className={ cx(rowClassName) }>
          <Typography variant='small' as='span'>{ `${orderState} (${lineItemQuantity} items)` }</Typography>
        </div>
        <div className={ cx(rowClassName) }>
          <img className={ cx(imageClassName) } src={ imageUrl } />
        </div>
        <div className={ cx(rowClassName) }>
          <Typography variant='caption' as='span' className={ cx(titleClassName) }>Total</Typography>
          <Typography variant='caption' as='span'><Price price={ totalPrice } /></Typography>
        </div>
      </div>
      <Modal isOpen={ isModalOpen } onClose={ (): void => setIsModalOpen(false) } title='Order summary'>
        <div className={ cx(modalContentClassName) }>
          <Typography variant='h5'>{orderNumber}</Typography>
          <div className={ cx(itemsSectionClassName) }>
            {lineItems.map(item => (
              <LineItemInfo
                readOnly
                className={ cx(lineItemClassName) }
                key={ item.id }
                lineItem={ item }
              />
            ))}
          </div>
          {shippingPrice && <div className={ cx(footerRowClassName) }>
            <Typography className={ cx(titleClassName) } variant='caption' as='span'>Shipping</Typography>
            <Typography as='span'><Price price={ shippingPrice } /></Typography>
          </div>}
          <div className={ cx(footerRowClassName) }>
            <Typography className={ cx(titleClassName) } variant='caption' as='span'>Total</Typography>
            <Typography variant='h4'><Price price={ totalPrice } /></Typography>
          </div>
          <div className={ cx(infoSectionClassName) }>
            <div className={ cx(infoItemClassName) }>
              <Typography className={ cx(titleClassName) } variant='h6'>Order receiver</Typography>
              <Typography as='span'> { `${shippingAddress.firstName ?? ''} ${shippingAddress.lastName ?? ''}` }</Typography>
            </div>
            <div className={ cx(infoItemClassName) }>
              <Typography className={ cx(titleClassName) } variant='h6'>Receiver phone number</Typography>
              <Typography as='span'> { shippingAddress.phone ?? '-'}</Typography>
            </div>
            <div className={ cx(infoItemClassName) }>
              <Typography className={ cx(titleClassName) } variant='h6'>Address</Typography>
              <Typography as='span'> {`${shippingAddress.streetName ?? ''} ${shippingAddress.building ?? ''}`}</Typography>
              <Typography as='span'> {`${shippingAddress.state ?? ''} ${shippingAddress.city ?? ''} ${shippingAddress.postalCode ?? ''}`}</Typography>
            </div>
            <div className={ cx(infoItemClassName) }>
              <Typography className={ cx(titleClassName) } variant='h6'>E-mail</Typography>
              <Typography as='span'> { shippingAddress.email ?? '-' } </Typography>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
